/**
 * Simplified Review Status Service
 * Handles: Start Review, Complete Review, Send Notifications
 * Updates ReviewStatus list and sends confirmation emails
 */

/**
 * Mark review as started - update status and send confirmation
 */
export const markReviewStarted = async (graphService, data) => {
  console.log(data);
  const {
    contractId,
    contractRef,
    contractTitle,
    reviewerName,
    reviewerEmail,
    id,
  } = data;

  try {
    console.log("[REVIEW] Marking review as started", {
      contractRef,
      reviewerEmail,
      id,
    });

    // Update ReviewStatus list item Status = "In Progress" only if id exists
    if (id) {
      try {
        console.log("[REVIEW] Attempting to update ReviewStatus item:", id);
        const updateData = {
          Status: "In Progress",
          ReviewStartDate: new Date().toISOString(),
        };
        console.log("[REVIEW] Sending update with data:", updateData);
        const result = await graphService.updateListItem(
          "ReviewStatus",
          id,
          updateData,
        );
        console.log(
          '[REVIEW] ✅ ReviewStatus SUCCESSFULLY updated to "In Progress"',
          result,
        );
      } catch (error) {
        console.error("[REVIEW] ❌ FAILED to update ReviewStatus list:", {
          error: error.message,
          errorFull: error,
          id,
          data: {
            Status: "In Progress",
            ReviewStartDate: new Date().toISOString(),
          },
        });
        throw error;
      }
    } else {
      console.log("[REVIEW] No id provided - skipping ReviewStatus update");
      throw new Error("No id provided - cannot update ReviewStatus");
    }

    // Send confirmation email to reviewer (only if we have an email)
    if (reviewerEmail && reviewerEmail.trim()) {
      await graphService.sendEmail(
        [reviewerEmail],
        `📋 Review Started: ${contractRef}`,
        `<p>You have started reviewing <strong>${contractTitle}</strong>.</p>
         <p>Track Changes is enabled on the document. Please make any necessary edits or comments.</p>
         <p>When finished, click "Complete Review".</p>`,
      );
      console.log(
        "[REVIEW] Review started confirmation sent to",
        reviewerEmail,
      );
    } else {
      console.warn(
        "[REVIEW] No reviewer email provided - skipping email notification",
      );
    }
  } catch (error) {
    console.error("[REVIEW] Error marking review started:", error);
    throw error;
  }
};

/**
 * Mark review as complete - update status, revoke access, send confirmation
 */
export const markReviewComplete = async (graphService, data) => {
  const {
    contractId,
    contractRef,
    contractTitle,
    reviewerName,
    reviewerEmail,
    id,
  } = data;

  try {
    console.log("[REVIEW] Marking review as complete", {
      contractRef,
      reviewerEmail,
      id,
    });

    // Update ReviewStatus list item Status = "Complete" only if id exists
    if (id) {
      try {
        console.log(
          "[REVIEW] Attempting to update ReviewStatus item to Complete:",
          id,
        );
        const updateData = {
          Status: "Complete",
          ReviewCompletedDate: new Date().toISOString(),
        };
        console.log("[REVIEW] Sending update with data:", updateData);
        const result = await graphService.updateListItem(
          "ReviewStatus",
          id,
          updateData,
        );
        console.log(
          '[REVIEW] ✅ ReviewStatus SUCCESSFULLY updated to "Complete"',
          result,
        );
      } catch (error) {
        console.error("[REVIEW] ❌ FAILED to update ReviewStatus list:", {
          error: error.message,
          errorFull: error,
          id,
          data: {
            Status: "Complete",
            ReviewCompletedDate: new Date().toISOString(),
          },
        });
        throw error;
      }
    } else {
      console.log(
        "[REVIEW] No id provided - skipping ReviewStatus update",
      );
      throw new Error("No id provided - cannot update ReviewStatus");
    }

    // Send confirmation to reviewer (only if we have an email)
    if (reviewerEmail && reviewerEmail.trim()) {
      const reviewerMessage = `
        <h2>✅ Review Complete</h2>
        <p>Thank you for reviewing <strong>${contractRef}</strong>.</p>
        <p>Your review has been submitted and you no longer have access to this document.</p>
        <p>You can close this window.</p>
      `;

      await graphService.sendEmail(
        [reviewerEmail],
        `✅ Review Complete: ${contractRef}`,
        reviewerMessage,
      );
      console.log(
        "[REVIEW] Review complete confirmation sent to",
        reviewerEmail,
      );
    } else {
      console.warn(
        "[REVIEW] No reviewer email provided - skipping email notification",
      );
    }
  } catch (error) {
    console.error("[REVIEW] Error marking review complete:", error);
    throw error;
  }
};

// Export as object for backward compatibility
export const reviewStatusService = {
  markReviewComplete: markReviewComplete,
  markReviewStarted: markReviewStarted,
};
